import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getClosingRetrieveQueryKey,
  useClosingRetrieveSuspense,
} from "gen/endpoints/closing/closing";
import {
  useFundFamilyEventRetrieve,
  useFundFamilyEventUpdate,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyEventEditDataRequest } from "gen/model/fundFamilyEventEditDataRequest";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";

export interface ClosingEditDateModalProps {
  fundFamilyId: string;
  closingId: string;
  onCloseComplete(): void;
}

export const ClosingEditDateModal: React.FC<ClosingEditDateModalProps> = ({
  fundFamilyId,
  closingId,
  onCloseComplete,
}) => {
  const { t } = useTranslation(["closing", "common"]);
  const queryClient = useQueryClient();

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const { data: closingData } = useClosingRetrieveSuspense(
    fundFamilyId,
    closingId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    closingData.fund_family_event_id,
    {
      query: {
        enabled: !!closingData,
      },
    },
  );

  const { mutateAsync: updateDate } = useFundFamilyEventUpdate({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getClosingRetrieveQueryKey(fundFamilyId, closingId),
        });
      },
    },
  });

  const onSubmit = async (data: FundFamilyEventEditDataRequest) => {
    await updateDate({
      fundFamilyId,
      fundFamilyEventId: closingData.fund_family_event_id,
      data: {
        effective_date: data.effective_date,
        posted_date: data.effective_date,
        capital_call_effective_date: null,
        capital_call_posted_date: null,
        notes: fundFamilyEventData?.notes ?? "",
        distribution_withholding_upload_document_id:
          fundFamilyEventData?.distribution_withholding_upload_document_id ??
          null,
        celery_workflow_id: fundFamilyEventData?.celery_workflow_id ?? null,
        notice_generation_celery_workflow_id:
          fundFamilyEventData!.notice_generation_celery_workflow_id ?? null,
      },
    });
  };

  const form = useForm<FundFamilyEventEditDataRequest>({
    defaultValues: {
      effective_date: fundFamilyEventData?.effective_date,
      posted_date: fundFamilyEventData?.posted_date,
      notes: fundFamilyEventData?.notes ?? "",
      distribution_withholding_upload_document_id:
        fundFamilyEventData?.distribution_withholding_upload_document_id ??
        null,
      notice_generation_celery_workflow_id:
        fundFamilyEventData?.notice_generation_celery_workflow_id ?? null,
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Form form={form} onSubmit={onSubmit} onSuccess={onClose}>
          <ModalHeader>
            {t("closing:editDateModal.title")}
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <FormDateInput
              name="effective_date"
              title={t("closing:editDateModal.dateInputTitle")}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={onClose} autoFocus={true} />}
              confirm={
                <FormSubmitButton>
                  {t("closing:editDateModal.submitBtnText")}
                </FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
